import { ThemeProvider, CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

import "./i18n";

import { AuthContext, AuthProvider, useAuth } from "./auth";
import KeycloackButtonProvider from "./components/KeycloackButtonProvider";
import { routeTree } from "./routeTree.gen";
import theme from "./theme";
import "./style.css";
import { axiosInstance } from "./api/axios-client";

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface RegisterRouterContext {
    auth: AuthContext;
  }
}

const queryClient = new QueryClient();

const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    queryClient,
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

axiosInstance.interceptors.request.use((config) => {
  const existingTenant = config.headers?.get ? config.headers.get('tenant') : (config.headers as any)?.['tenant'];
  const defaultHeaderTenant = (axiosInstance.defaults.headers.common as any)?.['tenant'];
  if (!existingTenant && !defaultHeaderTenant) {
    if (config.headers?.set) {
      config.headers.set('tenant', 'default-domain');
    } else if (config.headers) {
      (config.headers as any)['tenant'] = 'default-domain';
    }
  }
  return config;
});

function InnerApp() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth }} />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <KeycloackButtonProvider>
          <AuthProvider>
            <InnerApp />
          </AuthProvider>
        </KeycloackButtonProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
